import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaHistory, FaAward, FaHandshake, FaHeart, FaEdit, FaUser, FaCamera } from 'react-icons/fa';
import TeamImageCropper from '../components/team/TeamImageCropper';
import TeamMemberEditor from '../components/team/TeamMemberEditor';
import Message from '../components/ui/Message';
import Loader from '../components/ui/Loader';
import api from '../utils/api';

const TeamPage = () => {
  // Default team members data that will always be available
  const defaultTeamMembers = [
    {
      id: 1,
      name: '李志华',
      title: '董事长',
      bio: '作为南昌市玉洒贸易有限公司的创始人兼董事长，李志华先生凭借其敏锐的商业洞察力和卓越的领导才能，成功将公司从1995年的小型酒水贸易企业发展为如今在江西省内具有重要影响力的五粮液官方授权经销商。他在酒水行业拥有超过25年的丰富经验，精通产品质量把控和品牌建设，在她的带领下，公司连续多年获得各项工商奖项，建立了良好的市场声誉。不仅专注于企业发展，还积极参与社会公益事业，展现了杰出的企业家社会责任感。',
      image: '/images/team/lizhihua.jpg',
      achievements: [
        '江西省优秀民营企业家',
        '南昌市西湖区抗击新冠肺炎疫情先进个人',
        '泸州老窖2024年副会长单位代表',
        '南昌市工商联常委'
      ]
    },
    {
      id: 2,
      name: '吴小妹',
      title: '总经理',
      bio: '吴小妹女士作为南昌市玉洒贸易有限公司的总经理，主要负责公司的日常运营管理和业务发展。她在加入公司前，曾在国内知名白酒企业担任区域销售总监，拥有丰富的销售和团队管理经验。自2015年加入本公司以来，吴女士凭借其专业的市场洞察力和出色的执行能力，成功推动了公司从鼎天实业到玉洒贸易的品牌转型，实现了年销售额的持续增长。她特别擅长客户关系维护和渠道建设，为公司建立了稳定的业务网络和合作伙伴关系。',
      image: '/images/team/wuxiaomei.jpg',
      achievements: [
        '江西省优秀经理人',
        '南昌市"三八"红旗手',
        '2023年度酒类行业杰出营销人才',
        '公司业绩连续五年保持两位数增长'
      ]
    },
    {
      id: 3,
      name: '李俊杰（Junjie Li）',
      title: '首席技术官',
      bio: '李俊杰先生负责公司的技术战略和数字化转型。他在美国接受了高等教育，本科毕业于威斯康辛大学麦迪逊分校计算机科学专业，随后在杜克大学获得了商业分析硕士学位。李先生在加入家族企业前，曾在美国硅谷科技公司工作多年，积累了丰富的技术和数据分析经验。回国后，他主导了公司的信息化建设，开发了一套完整的库存管理和客户关系管理系统，大大提升了公司的运营效率和客户服务质量。同时，他还负责了公司电子商务平台的构建，成功拓展了线上销售渠道，为公司在数字时代的发展奠定了坚实基础。',
      image: '/images/team/lijunjie.jpg',
      education: '威斯康辛大学麦迪逊分校金融学士，杜克大学金融科技硕士',
      socialMedia: {
        linkedin: 'https://www.linkedin.com/in/junjie-li-547136221/'
      },
      achievements: [] 
    }
  ];

  const [teamMembers, setTeamMembers] = useState(defaultTeamMembers);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [showEditor, setShowEditor] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiAvailable, setApiAvailable] = useState(true);
  
  // Get auth state to check if user is admin
  const { user, isAuthenticated } = useSelector(state => state.auth);
  const isAdmin = isAuthenticated && user && user.is_admin;
  
  // Fetch team members from API
  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        setLoading(true);
        
        // Attempt to fetch team members from the API
        try {
          const response = await api.get('/team');
          if (response.data && Array.isArray(response.data)) {
            setTeamMembers(response.data);
          }
        } catch (apiError) {
          console.log('Using default team member data:', apiError);
          // If API fails, continue with default data
          setApiAvailable(false);
        }
      } catch (err) {
        console.error("Error handling team members:", err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTeamMembers();
  }, []);
  
  const handleEditPhoto = (member) => {
    if (!apiAvailable) {
      setError('图片上传功能暂时不可用，请联系管理员。');
      return;
    }
    
    setSelectedMember(member);
    setShowCropper(true);
    setShowEditor(false);
    setError(''); // Clear any previous errors
  };
  
  const handleEditInfo = (member) => {
    setSelectedMember(member);
    setShowEditor(true);
    setShowCropper(false);
    setError(''); // Clear any previous errors
  };
  
  const handleSavePhoto = (memberId, newImagePath) => {
    // Update the team member's image path in the local state
    const updatedMembers = teamMembers.map(member => 
      member.id === memberId 
        ? { ...member, image: newImagePath }
        : member
    );
    
    setTeamMembers(updatedMembers);
    setShowCropper(false);
    setSelectedMember(null);
    setSuccess(`${teamMembers.find(m => m.id === memberId)?.name || '成员'}的照片已更新。由于技术原因，您的更改可能不会立即保存到服务器。`);
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccess('');
    }, 5000);
  };
  
  const handleSaveInfo = (memberId, updatedInfo) => {
    // Update the team member's info in the local state
    const updatedMembers = teamMembers.map(member => 
      member.id === memberId 
        ? { ...member, ...updatedInfo }
        : member
    );
    
    setTeamMembers(updatedMembers);
    setShowEditor(false);
    setSelectedMember(null);
    setSuccess(`${updatedInfo.name || '成员'}的信息已更新。由于技术原因，您的更改可能不会立即保存到服务器。`);
    
    // Clear success message after 5 seconds
    setTimeout(() => {
      setSuccess('');
    }, 5000);
  };
  
  const handleLocalImageUpdate = (memberId, file) => {
    // This is a fallback method when the API isn't available
    // It uses a FileReader to display the image locally without saving to server
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const imageUrl = e.target.result;
      
      // Update the team member's image path in local state only
      const updatedMembers = teamMembers.map(member => 
        member.id === memberId 
          ? { ...member, image: imageUrl }
          : member
      );
      
      setTeamMembers(updatedMembers);
      setShowCropper(false);
      setSelectedMember(null);
      setSuccess(`${teamMembers.find(m => m.id === memberId)?.name || '成员'}的照片已临时更新。请注意，由于服务器限制，此更改仅在当前会话有效。`);
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess('');
      }, 5000);
    };
    
    reader.readAsDataURL(file);
  };
  
  const handleCancelEdit = () => {
    setShowCropper(false);
    setShowEditor(false);
    setSelectedMember(null);
    setError(''); // Clear any errors when canceling
  };

  // Fixed image error handler that won't create an infinite loop
  const handleImageError = (e, memberName) => {
    console.log(`Image failed to load for ${memberName}`);
    
    // Set a data attribute to prevent infinite loop when trying fallbacks
    if (e.target.getAttribute('data-fallback') === 'tried') {
      // Already tried the fallback, render the placeholder icon directly
      renderFallbackIcon(e.target, memberName);
      return;
    }
    
    // Mark that we've tried using a fallback
    e.target.setAttribute('data-fallback', 'tried');
    
    // Try the backup image
    e.target.src = '/images/default-avatar.png';
  };
  
  // Separate function to render fallback icon, to avoid redundant code
  const renderFallbackIcon = (imgElement, memberName) => {
    // Hide the failed image
    imgElement.style.display = 'none';
    
    const parent = imgElement.parentNode;
    if (parent) {
      // Add flex styling to parent container
      parent.classList.add('flex', 'items-center', 'justify-center', 'bg-gray-200');
      
      // Check if we already added the fallback icon
      if (!parent.querySelector('.fallback-icon')) {
        const iconEl = document.createElement('div');
        iconEl.className = 'fallback-icon';
        iconEl.innerHTML = `
          <div class="flex flex-col items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-20 h-20 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <span class="mt-2 text-gray-500">${memberName || '团队成员'}</span>
          </div>
        `;
        parent.appendChild(iconEl);
      }
    }
  };

  return (
    <div className="team-page">
      <div className="bg-gray-50 py-8 px-4 rounded-xl mb-8">
        <div className="container mx-auto">
          <h1 className="text-4xl font-bold mb-2">管理团队</h1>
          <div className="text-gray-600">我们的领导与专业团队</div>
        </div>
      </div>

      <div className="container mx-auto px-4 mb-16">
        {success && (
          <Message variant="success" onClose={() => setSuccess('')}>
            {success}
          </Message>
        )}
        
        {error && (
          <Message variant="error" onClose={() => setError('')}>
            {error}
          </Message>
        )}
        
        {!apiAvailable && isAdmin && (
          <Message variant="warning" onClose={() => setError('')}>
            警告：团队管理API不可用。数据更改将不会保存到服务器。请联系系统管理员解决此问题。
          </Message>
        )}
        
        <div className="text-center mb-12">
          <p className="text-lg text-gray-700 max-w-3xl mx-auto">
            南昌市玉洒贸易有限公司拥有一支经验丰富、专业高效的管理团队。我们的领导者在酒类行业拥有深厚的专业知识和丰富的市场经验，致力于为客户提供优质的产品和卓越的服务。
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : (
          <div className="space-y-20">
            {teamMembers.map((member, index) => (
              <div 
                key={member.id} 
                className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 items-center`}
              >
                {/* 团队成员照片 - 竖版 */}
                <div className="lg:w-1/3">
                  <div className="bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-shadow relative">
                    {/* Admin edit photo button */}
                    {isAdmin && (
                      <button
                        onClick={() => handleEditPhoto(member)}
                        className="absolute top-2 right-2 bg-red-700 text-white p-2 rounded-full hover:bg-red-800 transition-colors z-10"
                        title="编辑照片"
                      >
                        <FaCamera />
                      </button>
                    )}
                    
                    <div className="overflow-hidden rounded-lg h-[400px] w-[300px] mx-auto bg-gray-100 relative">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                        onError={(e) => handleImageError(e, member.name)}
                        key={`${member.id}-image-${new Date().getTime()}`} // Dynamic key to force re-render on image change
                      />
                    </div>
                  </div>
                </div>

                {/* 团队成员介绍 */}
                <div className="lg:w-2/3">
                  <div className="bg-white p-6 rounded-xl shadow-md relative">
                    {/* Admin edit info button */}
                    {isAdmin && (
                      <button
                        onClick={() => handleEditInfo(member)}
                        className="absolute top-2 right-2 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors"
                        title="编辑信息"
                      >
                        <FaEdit />
                      </button>
                    )}
                    
                    <h2 className="text-3xl font-bold text-red-800 mb-1">{member.name}</h2>
                    <p className="text-xl text-gray-600 mb-4">{member.title}</p>
                    
                    <div className="border-l-4 border-red-700 pl-4 my-4">
                      {member.education && (
                        <div className="flex items-center mb-2">
                          <FaHistory className="text-gray-600 mr-2" />
                          <span className="text-gray-700">{member.education}</span>
                        </div>
                      )}
                      
                      {member.socialMedia && member.socialMedia.linkedin && (
                        <div className="flex items-center">
                          <FaUser className="text-blue-700 mr-2" />
                          <a 
                            href={member.socialMedia.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            LinkedIn 个人主页
                          </a>
                        </div>
                      )}
                    </div>
                    
                    <p className="text-gray-700 leading-relaxed mb-6">
                      {member.bio}
                    </p>
                    
                    {member.achievements && member.achievements.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold mb-2">主要成就:</h3>
                        <ul className="list-disc pl-5 text-gray-700 space-y-1">
                          {member.achievements.map((achievement, idx) => (
                            <li key={idx}>{achievement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Image Cropper Modal */}
        {showCropper && selectedMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="max-w-md w-full">
              <TeamImageCropper 
                member={selectedMember}
                onSave={handleSavePhoto}
                onCancel={handleCancelEdit}
                onLocalUpdate={handleLocalImageUpdate}
                apiAvailable={apiAvailable}
              />
            </div>
          </div>
        )}
        
        {/* Team Member Editor Modal */}
        {showEditor && selectedMember && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="max-w-lg w-full">
              <TeamMemberEditor 
                member={selectedMember}
                onSave={handleSaveInfo}
                onCancel={handleCancelEdit}
                apiAvailable={apiAvailable}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPage;